// holidays-tab.component.ts

import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ColDef } from 'ag-grid-enterprise';
import { dateFilterParamsLong } from '@rcs/utils';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErsGridComponent } from '@rcs/grid';
import { UpdateOptionalConfirmDialogComponent } from '../../../dialogs/update-optional-confirm-dialog/update-optional-confirm-dialog.component';
import { SuccessDialogComponent } from '../../../dialogs/success-dialog/success-dialog';
import { HolidayRow } from '../../../models/leave.model';
import { formatDateLong, getDayName, parseLocalDate, toISODate } from '../../../utils/date.utils';
import { ChooseOptionalHolidayDialogComponent } from '../../../dialogs/choose-optional-holiday-dialog/choose-optional-holiday-dialog.component';
import { LeaveService } from '../../../services/leave.service';
import { TranslocoPipe } from '@jsverse/transloco';


const GRID_CELL_STYLE = { fontSize: '13px', color: '#1e293b' };

@Component({
  selector: 'ers-holidays-tab',
  standalone: true,
  imports: [
    TranslocoPipe,CommonModule, MatDialogModule, ErsGridComponent],
  templateUrl: './holidays-tab.component.html',
  styleUrls: ['./holidays-tab.component.scss'],
})
export class HolidaysTabComponent implements OnInit {
  private nextId = 1;
  private destroyRef = inject(DestroyRef);
  /** Maps local numeric grid ID → backend UUID */
  private backendIdMap = new Map<number, string>();
  private allHolidays: HolidayRow[] = [];
  holidays: HolidayRow[] = [];
  isLoadingHolidays = false;

  // ── Optional Holidays (employee view) ─────────────────
  myOptionalHolidays: any[] = [];
  isLoadingOptional = false;
  /** Supervisor ID used in the last successful submission */
  lastSubmittedSupervisorId = '';

  colDefs: ColDef[] = [
    {
      headerName: 'Holiday Name',
      field: 'name',
      flex: 2,
      wrapText: true,
      autoHeight: true,
      minWidth: 200,
      filter: 'agSetColumnFilter',
      cellRenderer: (params: any) => {
        const isMandatory = params.data?.optional === 'Mandatory';
        const star = isMandatory
          ? `<span style="color:#FFC107;margin-right:5px;font-size:14px;line-height:1;">★</span>`
          : `<span style="display:inline-block;width:19px;"></span>`;
        return `<span style="display:flex;align-items:center;font-size:13px;color:#1e293b;">${star}${params.value ?? ''}</span>`;
      },
    },
    { headerName: 'Date',            field: 'date',     flex: 1, filter: 'agDateColumnFilter', filterParams: dateFilterParamsLong, cellStyle: GRID_CELL_STYLE },
    { headerName: 'Day',             field: 'day',      flex: 1, filter: 'agSetColumnFilter', cellStyle: GRID_CELL_STYLE },
    { headerName: 'Type',            field: 'type',     flex: 1, filter: 'agSetColumnFilter', cellStyle: GRID_CELL_STYLE },
    { headerName: 'Optional Holiday',field: 'optional', flex: 1, filter: 'agSetColumnFilter', cellStyle: GRID_CELL_STYLE },
  ];

  optionalColDefs: ColDef[] = [
    { headerName: 'Holiday Name', field: 'holidayName', flex: 2, filter: 'agSetColumnFilter', cellStyle: GRID_CELL_STYLE },
    { headerName: 'Date',         field: 'holidayDate', flex: 1, filter: 'agDateColumnFilter', filterParams: dateFilterParamsLong, cellStyle: GRID_CELL_STYLE },
    { headerName: 'Type',         field: 'holidayType', flex: 1, filter: 'agSetColumnFilter', cellStyle: GRID_CELL_STYLE },
    { headerName: 'Status',       field: 'status',      flex: 1, filter: 'agSetColumnFilter', cellStyle: GRID_CELL_STYLE,
      cellRenderer: (params: any) => {
        const s = (params.value ?? '').toLowerCase();
        const color = s === 'approved' ? '#16a34a' : s === 'rejected' ? '#dc2626' : '#d97706';
        return `<span style="color:${color};font-weight:600;">${params.value ?? '–'}</span>`;
      }
    },
  ];

  gridOptions = {
    suppressCellFocus: true,
    suppressHorizontalScroll: true,
    pagination: true,
    paginationPageSize: 5,
    paginationPageSizeSelector: false,
    rowHeight: 44,
  };

  constructor(private dialog: MatDialog, private leaveService: LeaveService) {}

  ngOnInit(): void {
    this.loadHolidays();
  }

  private loadHolidays(): void {
    this.isLoadingHolidays = true;
    this.leaveService.getHolidays().subscribe(backendHolidays => {
      this.isLoadingHolidays = false;
      this.nextId = 1;
      this.backendIdMap.clear();
      this.holidays = backendHolidays.map(h => {
        const id = this.nextId++;
        this.backendIdMap.set(id, h.id);
        const date = parseLocalDate(h.holidayDate);
        const isoDate = toISODate(date);
        return {
          id,
          name: h.holidayName,
          date: formatDateLong(date),
          isoDate,
          day: getDayName(date),
          type: h.holidayType,
          optional: h.isOptional ? 'Optional' : 'Mandatory',
          countryId: h.applicableToCountryId ?? null,
          stateId: h.applicableToStateId ?? null,
          notes: h.notes ?? null,
        } as HolidayRow;
      });
      // Sort newest date first
      this.holidays.sort((a, b) => {
        const da = parseLocalDate(a.isoDate);
        const db = parseLocalDate(b.isoDate);
        return db.getTime() - da.getTime();
      });
      this.allHolidays = [...this.holidays];
      this.loadMyOptionalHolidays();
    });
  }

  loadMyOptionalHolidays(): void {
    this.isLoadingOptional = true;
    this.leaveService.getMyOptionalHolidays()
      .pipe(
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(items => {
        this.myOptionalHolidays = (items ?? []).map((item: any) => ({
          // backend sometimes returns upper‑case keys, so check both
          holidayId:   item.holidayId   ?? item.HOLIDAY_ID   ?? null,
          holidayName: item.holidayName ?? item.HOLIDAY_NAME ?? '–',
          holidayDate: (item.holidayDate ?? item.HOLIDAY_DATE)
            ? formatDateLong(parseLocalDate(item.holidayDate ?? item.HOLIDAY_DATE))
            : '–',
          holidayType: item.holidayType ?? item.HOLIDAY_TYPE ?? 'Optional Holiday',
          status: item.status ?? item.STATUS ?? 'Pending',
        }));
        this.isLoadingOptional = false;
      });
  }

  // ── Optional Holiday: Available holidays in calendar ──────────────────────
  get availableOptionalHolidays(): HolidayRow[] {
    return this.allHolidays.filter(h => h.optional === 'Optional');
  }

  /** True when the employee has already submitted optional holidays */
  get hasSubmittedOptionalHolidays(): boolean {
    return this.myOptionalHolidays.length > 0;
  }

  /**
   * Reverse-map the submitted optional holidays back to local grid IDs
   * so they can be pre-selected when the dialog is opened in update mode.
   */
  get preSelectedHolidayLocalIds(): number[] {
    const result: number[] = [];
    for (const opt of this.myOptionalHolidays) {
      if (!opt.holidayId) continue;
      for (const [localId, backendUUID] of this.backendIdMap) {
        if (backendUUID === opt.holidayId) {
          result.push(localId);
          break;
        }
      }
    }
    return result;
  }

  onChooseOptionalHoliday(): void {
    const isUpdate = this.hasSubmittedOptionalHolidays;

    const ref = this.dialog.open(ChooseOptionalHolidayDialogComponent, {
      width: '680px',
      panelClass: 'lm-dialog-panel',
      data: {
        availableHolidays: this.availableOptionalHolidays,
        preSelectedIds: isUpdate ? this.preSelectedHolidayLocalIds : [],
        isUpdateMode: isUpdate,
        previousSupervisorId: isUpdate ? this.lastSubmittedSupervisorId : '',
      },
    });

    ref.afterClosed().subscribe((result: any | null) => {
      if (!result) return;

      const submitFn = () => {
        const { selectedHolidayIds, supervisorId } = result;
        const selectedRows = this.allHolidays.filter(h => selectedHolidayIds.includes(h.id));
        if (!selectedRows.length) return;

        const requests$ = selectedRows.map(row => {
          const backendId = this.backendIdMap.get(row.id);
          if (!backendId) return of(null);
          return this.leaveService.requestOptionalHoliday(backendId).pipe(catchError(() => of(null)));
        });

        forkJoin(requests$).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          // Remember the supervisor used for next update pre-fill
          this.lastSubmittedSupervisorId = supervisorId ?? '';

          this.dialog.open(SuccessDialogComponent, {
            width: '500px',
            panelClass: 'lm-dialog-panel',
            data: {
              title: isUpdate
                ? 'Your optional holidays have been updated successfully!'
                : 'Your optional holidays are submitted successfully!',
              desc: 'Your selected optional holidays can be viewed by the selected Supervisor and HR.',
            },
          });
          this.loadMyOptionalHolidays();
        });
      };

      if (isUpdate) {
        // Show confirmation dialog before applying the update
        const confirmRef = this.dialog.open(UpdateOptionalConfirmDialogComponent, {
          width: '500px',
          panelClass: 'lm-dialog-panel',
        });
        confirmRef.afterClosed().subscribe((confirmed: boolean) => {
          if (confirmed) submitFn();
        });
      } else {
        submitFn();
      }
    });
  }

}

